// frontend/js/queuedBotsIndicator.js
/**
 * Queued Bots Indicator
 * Shows a small non-intrusive notification when bots are waiting to interact
 */

class QueuedBotsIndicator {
  constructor() {
    this.indicator = null;
    this.currentCount = 0;
  }

  /**
   * Initialize the indicator and socket listener
   */
  initialize(socket) {
    if (!socket) {
      console.warn('⚠️ [QueuedBotsIndicator] No socket provided');
      return;
    }

    // Create the indicator element
    this.createIndicator();

    // Listen for queue updates
    socket.on('queuedBotsWaiting', (data) => {
      this.updateIndicator(data.count, data.message);
    });

    console.log('✅ [QueuedBotsIndicator] Initialized');
  }

  /**
   * Create the indicator DOM element
   */
  createIndicator() {
    this.indicator = document.createElement('div');
    this.indicator.id = 'queuedBotsIndicator';
    this.indicator.className = 'queued-bots-indicator';
    this.indicator.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.85);
      color: #00ffff;
      padding: 12px 18px;
      border-radius: 8px;
      border: 2px solid #00ffff;
      box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
      font-family: 'bithigh', monospace;
      font-size: 14px;
      z-index: 9999;
      display: none;
      transition: all 0.3s ease;
      cursor: pointer;
      backdrop-filter: blur(5px);
    `;

    this.indicator.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px;">
        <span class="pulse-dot" style="width: 10px; height: 10px; border-radius: 50%; background: #00ffff; animation: pulse 1.5s ease-in-out infinite;"></span>
        <span id="queuedBotsText">0 bots waiting</span>
      </div>
    `;

    // Add CSS for pulse animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.2); }
      }
      .queued-bots-indicator:hover {
        transform: translateY(-2px);
        box-shadow: 0 0 30px rgba(0, 255, 255, 0.7);
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(this.indicator);
  }

  /**
   * Update the indicator with new count
   */
  updateIndicator(count, message) {
    if (!this.indicator) return;

    this.currentCount = count;
    const textElement = this.indicator.querySelector('#queuedBotsText');

    if (count > 0) {
      textElement.textContent = message || `${count} bot${count > 1 ? 's' : ''} waiting`;
      this.indicator.style.display = 'block';

      // Animate in
      setTimeout(() => {
        this.indicator.style.opacity = '1';
        this.indicator.style.transform = 'translateY(0)';
      }, 10);

      console.log(`📬 [QueuedBotsIndicator] ${count} bots waiting`);
    } else {
      // Fade out
      this.indicator.style.opacity = '0';
      this.indicator.style.transform = 'translateY(10px)';
      setTimeout(() => {
        this.indicator.style.display = 'none';
      }, 300);

      console.log(`✅ [QueuedBotsIndicator] Queue cleared`);
    }
  }

  /**
   * Get current queue count
   */
  getCount() {
    return this.currentCount;
  }
}

// Create singleton instance
const queuedBotsIndicator = new QueuedBotsIndicator();

// Auto-initialize when socket is available
window.addEventListener('load', () => {
  // Wait for socket to be available
  const checkSocket = () => {
    if (window.socket && window.socket.connected) {
      queuedBotsIndicator.initialize(window.socket);
    } else {
      setTimeout(checkSocket, 500);
    }
  };
  checkSocket();
});

// Export for global access
window.queuedBotsIndicator = queuedBotsIndicator;

export default queuedBotsIndicator;
